import { useEffect, useState } from "react";

import { Fabric, NetworkPort, Node } from "src/gen/schema/models/models";
import { PortConnection } from "src/gen/schema/models/types";

// private types
type Registry<T> = Record<string, T>;
type NodeRegistry = Registry<Node>;
type PortRegistry = Registry<NetworkPort>;
type PluggableRegistry = Registry<string>;
type GetterById<T> = (id?: string) => T | undefined;
type GetterByIdAndName<T> = (id?: string, name?: string) => T | undefined;
type GetterByIdAndIndex<T> = (id?: string, index?: number) => T | undefined;

// public types
export type NodeGetter = GetterById<Node>;
export type PortGetter = GetterById<NetworkPort>;
export type PortByIndexGetter = GetterByIdAndIndex<NetworkPort>;
export type PortByNameGetter = GetterByIdAndName<NetworkPort>;
export type PluggableGetter = GetterByIdAndName<string>;

export interface FabricHelpers {
  getPortById: PortGetter;
  getPortByName: PortByNameGetter;
  getPortByIndex: PortByIndexGetter;
  getPluggableByName: PluggableGetter;
  getNode: NodeGetter;
}

export const useFabricHelpers = (fabric?: Fabric): FabricHelpers => {
  const [helpers, setHelpers] = useState<FabricHelpers>({
    getPortById: () => undefined,
    getPortByName: () => undefined,
    getPortByIndex: () => undefined,
    getPluggableByName: () => undefined,
    getNode: () => undefined
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (fabric) {
        const pluggables: PluggableRegistry = {};
        const ports: PortRegistry = {};
        const nodes: NodeRegistry = {};
        fabric.nodes.forEach((node) => {
          // TODO: Does this also need to account for group(s)
          nodes[node.deviceId] = node;
          nodes[node.id] = node;
          node.ports.reduce((out: PortRegistry, port) => {
            out[`${node.id}.${port.index}`] = port;
            out[`${node.id}.${port.name}`] = port;
            out[`${node.deviceId}.${port.index}`] = port;
            out[`${node.deviceId}.${port.name}`] = port;
            out[port.id] = port;
            return out;
          }, ports);
          /**
           * Using a fabric's connections array, map each port to its pluggable,
           * as defined in the cabling plan.
           */
          node.connections.reduce(
            (out: PluggableRegistry, conn: PortConnection) => {
              const { local, remote, pluggable, remotePluggable } = conn;

              // Map local port to its pluggable
              if (local?.nodeId && local?.portName && pluggable) {
                out[`${local?.nodeId}.${local?.portName}`] = pluggable;
              }

              // Map remote port to its pluggable
              if (remote?.nodeId && remote?.portName && remotePluggable) {
                out[`${remote?.nodeId}.${remote?.portName}`] = remotePluggable;
              }

              return out;
            },
            pluggables
          );
        });

        setHelpers({
          getPortById: (id: string = "") => ports[id],
          getPortByName: (
            nodeId: string = "",
            portName: string = ""
          ): NetworkPort | undefined => {
            return ports[`${nodeId}.${portName}`];
          },
          getPortByIndex: (
            nodeId: string = "",
            index: number = 0
          ): NetworkPort | undefined => {
            return ports[`${nodeId}.${index}`];
          },
          getPluggableByName: (
            nodeId: string | undefined,
            portName: string | undefined
          ): string | undefined => {
            if (!nodeId || !portName) return undefined;
            return pluggables[`${nodeId}.${portName}`];
          },
          getNode: (id: string = ""): Node | undefined => {
            return nodes[id];
          }
        });
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [fabric]);
  return helpers;
};
